import type { StatusDescriptor } from '@/shared/ui/status.model';
import type { RoomFeedbackStats } from './feedback.models';
import { ROOM_TYPES, RoomStatus, RoomType, RoomUsageState, isRoomType, roomStatus } from './room-usage.models';

/**
 * Status filter groups for the rooms list, matching the Overview "Room usage now" tiles. Each groups
 * whole room statuses (the ones the pills show, via roomStatus()), and every status is in exactly one
 * group, so a filter never picks a room whose pill says something else. The first status of a group
 * gives its chip icon and colour.
 */
export const ROOM_STATUS_GROUPS = {
    inCall: ['inCall'],
    inMeeting: ['inMeeting'],
    bookedVacant: ['bookedVacant'],
    occupied: ['occupied'],
    // Not in use now; "Starting soon" counts as free until the meeting starts, like "Free now".
    available: ['vacant', 'free', 'startingSoon'],
    noData: ['noData', 'unknown', 'notInCall']
} as const satisfies Record<string, readonly RoomStatus[]>;
export type RoomStatusGroup = keyof typeof ROOM_STATUS_GROUPS;
export const ROOM_STATUS_GROUP_KEYS = Object.keys(ROOM_STATUS_GROUPS) as RoomStatusGroup[];
export type RoomStatusFilter = 'all' | RoomStatusGroup;

/** Room type filter: every type, one type, or rooms whose type has not been set. */
export type RoomTypeFilter = 'all' | 'notSet' | RoomType;

/** Minimum seats choices for the capacity filter (0 = any). */
export const ROOM_CAPACITY_FILTERS = [0, 2, 4, 8, 12, 20, 50] as const;

export interface RoomFilters {
    status: RoomStatusFilter;
    type: RoomTypeFilter;
    /** Minimum effective capacity; 0 for any. A room without a known capacity only matches "any". */
    minCapacity: number;
}

export const DEFAULT_ROOM_FILTERS: RoomFilters = { status: 'all', type: 'all', minCapacity: 0 };

/** What the filters look at on a room row. */
export interface FilterableRoom {
    usage?: RoomUsageState | null;
    roomType?: string | null;
    /** Effective capacity (cloud-set, else sensor-reported). */
    capacity: number | null;
}

export function hasActiveRoomFilters(filters: RoomFilters): boolean {
    return filters.status !== 'all' || filters.type !== 'all' || filters.minCapacity > 0;
}

export function roomStatusGroup(status: RoomStatus): RoomStatusGroup {
    return ROOM_STATUS_GROUP_KEYS.find((group) => (ROOM_STATUS_GROUPS[group] as readonly RoomStatus[]).includes(status)) ?? 'noData';
}

export function matchesRoomStatus(usage: RoomUsageState | null | undefined, filter: RoomStatusFilter): boolean {
    return filter === 'all' || roomStatusGroup(roomStatus(usage)) === filter;
}

export function matchesRoomType(roomType: string | null | undefined, filter: RoomTypeFilter): boolean {
    if (filter === 'all') return true;
    if (filter === 'notSet') return !isRoomType(roomType);
    return roomType === filter;
}

export function matchesRoomCapacity(capacity: number | null, minCapacity: number): boolean {
    return minCapacity <= 0 || (capacity !== null && capacity >= minCapacity);
}

export function matchesRoomFilters(room: FilterableRoom, filters: RoomFilters): boolean {
    return matchesRoomStatus(room.usage, filters.status) && matchesRoomType(room.roomType, filters.type) && matchesRoomCapacity(room.capacity, filters.minCapacity);
}

export function filterRooms<T extends FilterableRoom>(rooms: T[], filters: RoomFilters): T[] {
    return hasActiveRoomFilters(filters) ? rooms.filter((room) => matchesRoomFilters(room, filters)) : rooms;
}

export interface RoomStatusChoice {
    group: RoomStatusGroup;
    count: number;
}

/**
 * The status chips to offer, with how many of `rooms` each would show: only groups some room is
 * in, plus the selected group so it can always be cleared. Pass the rooms the other filters leave.
 */
export function roomStatusChoices(rooms: FilterableRoom[], selected: RoomStatusFilter): RoomStatusChoice[] {
    const counts = new Map<RoomStatusGroup, number>();
    for (const room of rooms) {
        const group = roomStatusGroup(roomStatus(room.usage));
        counts.set(group, (counts.get(group) ?? 0) + 1);
    }
    return ROOM_STATUS_GROUP_KEYS.filter((group) => counts.has(group) || group === selected).map((group) => ({ group, count: counts.get(group) ?? 0 }));
}

/**
 * The room types to offer: the types rooms in scope have, then "notSet" if any room has none.
 * Empty (so the filter is hidden) while no room has a type, unless a type is selected.
 */
export function roomTypeChoices(rooms: FilterableRoom[], selected: RoomTypeFilter): Exclude<RoomTypeFilter, 'all'>[] {
    const present = new Set(rooms.map((room) => room.roomType));
    const types = ROOM_TYPES.filter((type) => present.has(type) || type === selected);
    if (!types.length && selected === 'all') return [];
    return rooms.some((room) => !isRoomType(room.roomType)) || selected === 'notSet' ? [...types, 'notSet'] : types;
}

/**
 * The minimum-seat thresholds to offer: those at least one room in scope meets, plus the selected one.
 * Empty (so the filter is hidden) while no room has a known capacity, unless a threshold is selected.
 */
export function roomCapacityChoices(rooms: FilterableRoom[], selected: number): number[] {
    const largest = Math.max(0, ...rooms.map((room) => room.capacity ?? 0));
    return ROOM_CAPACITY_FILTERS.filter((min) => min > 0 && (min <= largest || min === selected));
}

/** A room as the rooms list (cards and table) shows it. */
export interface RoomListRow extends FilterableRoom {
    id: string;
    appNamespaceId: string;
    displayName: string;
    code?: string;
    parentName?: string | null;
    systemName: string | null;
    controlSystemDeviceId?: string | null;
    /** Health of the room's control system; null without one. */
    status: StatusDescriptor | null;
    /** Devices in the room; null while unknown. */
    deviceCount: number | null;
    usage: RoomUsageState | null;
    /** The control system is not online, so `usage` is the last reported state. */
    usageStale: boolean;
    roomType: RoomType | null;
    /** Capacity set in the cloud (not the sensor-reported fallback). */
    cloudCapacity: number | null;
    roomStatus: RoomStatus;
    /** Feedback over the last ROOM_FEEDBACK_DAYS; null when the room has none (or feedback could not be loaded). */
    feedback: RoomFeedbackStats | null;
    /** `feedback.average`, flat so the rooms table can sort on it. */
    feedbackAverage: number | null;
}
